import React, { useEffect } from "react";

import { faArrowRightArrowLeft, faDollarSign } from "@fortawesome/pro-regular-svg-icons";

import {
	FormField,
	FormSubscribe,
	Icon,
	InputNumeric,
	InputWrapper,
	Spinner,
	Text,
	usePyForm,
} from "@performyard/py-components";

import { allotmentBudgetSchema } from "../types";

import { useSelectionSalarySummary } from "../../../../api/hooks/defs/compensation";

import { formatCurrency } from "../../utils";

const DEFAULT_ALLOTMENT_PERCENT = 0.03;

type AllotmentIncreaseInputProps = {
	totalSalary: number;
	value: number | null | undefined;
	onChange: (value: number | null | undefined) => void;
	onBlur: () => void;
};

const AllotmentIncreaseInput = ({ totalSalary, value, onChange, onBlur }: AllotmentIncreaseInputProps) => {
	const setFixed = (next: number | null | undefined): void => {
		if (next === null || next === undefined) {
			onChange(undefined);
			return;
		}
		onChange(next);
	};

	const percentValue = value != null && totalSalary > 0 ? (value / totalSalary) * 100 : null;
	const fixedDisplayValue = value ?? null;

	const handlePercentInputChange = (val: number | null): void => {
		if (val === null || totalSalary <= 0) {
			setFixed(undefined);
			return;
		}
		onChange(totalSalary * (val / 100));
	};

	const handleFixedInputChange = (val: number | null): void => {
		if (val === null) {
			setFixed(undefined);
			return;
		}
		onChange(val);
	};

	return (
		<div className="grid grid-cols-[1fr_24px_1fr] items-end gap-4">
			<InputWrapper id="allotment-increase-percent" label="Percent Increase">
				<InputNumeric
					allowEmpty
					value={percentValue}
					onChange={handlePercentInputChange}
					onBlur={onBlur}
					placeholder="Enter percentage"
					precision={2}
				/>
			</InputWrapper>
			<div className="flex h-8 items-center justify-center">
				<Icon icon={faArrowRightArrowLeft} color="neutral-9" size={16} />
			</div>
			<InputWrapper id="allotment-increase-fixed" label="Fixed Increase">
				<InputNumeric
					allowEmpty
					value={fixedDisplayValue}
					onChange={handleFixedInputChange}
					onBlur={onBlur}
					placeholder="Enter dollar amount"
					leftIcon={faDollarSign}
					useThousandsSeparator
					precision={2}
				/>
			</InputWrapper>
		</div>
	);
};

type CycleBudgetStepProps = {
	userSelectionId: string;
};

const CycleBudgetStep = ({ userSelectionId }: CycleBudgetStepProps) => {
	const { data, isLoading } = useSelectionSalarySummary(userSelectionId);
	const form = usePyForm<typeof allotmentBudgetSchema>();
	const totalSalary = data?.totalSalary ?? 0;

	useEffect(() => {
		if (isLoading || totalSalary <= 0) {
			return;
		}

		const { totalEligibleCompensation: previousTotal, allotmentIncreaseFixed: currentFixed } = form.getFormValues();

		if (
			previousTotal > 0 &&
			previousTotal !== totalSalary &&
			typeof currentFixed === "number" &&
			currentFixed > 0
		) {
			const impliedPercent = currentFixed / previousTotal;
			const nextFixed =
				Math.abs(impliedPercent - DEFAULT_ALLOTMENT_PERCENT) < 0.0001
					? totalSalary * DEFAULT_ALLOTMENT_PERCENT
					: totalSalary * impliedPercent;
			form.setFieldValue("allotmentIncreaseFixed", nextFixed);
		}

		if (previousTotal === 0 && (currentFixed === undefined || currentFixed === null)) {
			form.setFieldValue("allotmentIncreaseFixed", totalSalary * DEFAULT_ALLOTMENT_PERCENT);
		}

		form.setFieldValue("totalEligibleCompensation", totalSalary);
	}, [form, isLoading, totalSalary]);

	if (isLoading) {
		return (
			<div className="flex items-center justify-center py-12">
				<Spinner />
			</div>
		);
	}
	return (
		<div className="mx-auto flex max-w-[512px] flex-col gap-8">
			<div className="text-center">
				<Text size="sub-heading" weight="semi-bold">
					Create a Compensation Cycle
				</Text>
				<Text size="paragraph-2" weight="regular">
					Cycle Budget
				</Text>
			</div>

			<div className="flex flex-col gap-4 bg-neutral-1 p-6">
				<div>
					<Text size="caption">Total Eligible Compensation</Text>
					<Text size="heading-3" weight="semi-bold">
						{formatCurrency(totalSalary)}
					</Text>
				</div>

				<div className="flex flex-col gap-3">
					<Text size="caption" weight="regular">
						Define Budget
					</Text>
					<FormField<number | null | undefined>
						id="allotmentIncreaseFixed"
						input={({ value, onChange, onBlur }) => (
							<AllotmentIncreaseInput
								totalSalary={totalSalary}
								value={value as number | null | undefined}
								onChange={(v) => onChange(v as number | null | undefined)}
								onBlur={onBlur}
							/>
						)}
					/>
				</div>
				<div>
					<Text size="paragraph-3" weight="semi-bold">
						New Compensation Total
					</Text>
					<FormSubscribe
						id="allotmentIncreaseFixed-subscription"
						selector={(state: { values: { allotmentIncreaseFixed: number | null | undefined } }) =>
							state.values.allotmentIncreaseFixed
						}
					>
						{(allotmentIncreaseFixed) => (
							<div className="mt-2 rounded-1 bg-neutral-2 p-2">
								<Text size="paragraph-2" weight="semi-bold">
									{formatCurrency(totalSalary + Math.floor(allotmentIncreaseFixed ?? 0))}
								</Text>
							</div>
						)}
					</FormSubscribe>
				</div>
			</div>
		</div>
	);
};

export default CycleBudgetStep;
